/**
 * Passive Tree Editor - Grid-Based HTML Overlay Design Tool
 * Click grid cells to place nodes, shift+click to connect, ctrl+click to move.
 * Developer tool for creating and editing the passive skill tree.
 */

import {
  PassiveNode,
  PassiveNodeType,
  BranchId,
  NodeCategory,
} from '@shared/passives/PassiveTree';

import { ICONS, BRANCH_COLORS, NODE_SIZES, NODE_TYPE_COLORS, STAT_OPTIONS } from './IconLibrary';

// ========================
// Types
// ========================

type EditorNodeType = PassiveNodeType | 'attribute';

interface EditorNode {
  id: string;
  name: string;
  description: string;
  type: EditorNodeType;
  category: NodeCategory;
  branchId: BranchId | '';
  gridX: number;
  gridY: number;
  connections: string[];
  bonuses: Record<string, number>;
  isAttributeNode: boolean;
  keystoneEffect?: string;
}

interface UndoAction {
  type: 'place' | 'delete' | 'move' | 'connect' | 'disconnect' | 'edit';
  data: unknown;
}

// ========================
// Constants
// ========================

const GRID_W = 80;
const GRID_H = 80;
const CELL_SIZE = 48;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;
const MAX_UNDO = 30;

const NODE_TYPES: EditorNodeType[] = ['small', 'notable', 'keystone', 'attribute', 'branch_start', 'center'];
const NODE_TYPE_LABELS: Record<string, string> = {
  small: 'Small', notable: 'Notable', keystone: 'Keystone',
  attribute: 'Attribute', branch_start: 'Başlangıç', center: 'Merkez',
};

const BRANCHES: (BranchId | '')[] = ['demir', 'kale', 'avci', 'golge', 'firtina', 'ruhlar'];
const BRANCH_LABELS: Record<string, string> = {
  demir: 'Demir', kale: 'Kale', avci: 'Avcı', golge: 'Gölge', firtina: 'Fırtına', ruhlar: 'Ruhlar',
};

const CATEGORIES: NodeCategory[] = [
  'strength', 'agility', 'spirit', 'life', 'mana', 'defense', 'physical', 'attack_speed',
  'crit', 'fire', 'lightning', 'cold', 'earth', 'holy', 'evasion', 'speed', 'leech',
  'regen', 'dot', 'minion', 'energy_shield', 'spell',
];

// Grouped preset bonuses for quick one-click adding
const PRESET_GROUPS: { title: string; presets: { label: string; stat: string; value: number }[] }[] = [
  { title: '❤️ Savunma', presets: [
    { label: '%6 HP', stat: 'percentHp', value: 6 },
    { label: '%10 HP', stat: 'percentHp', value: 10 },
    { label: '%6 Zırh', stat: 'percentArmor', value: 6 },
    { label: '%6 Kaçınma', stat: 'percentEvasion', value: 6 },
    { label: '%8 ES', stat: 'percentEnergyShield', value: 8 },
    { label: '%5 HP Regen', stat: 'percentHpRegen', value: 5 },
  ]},
  { title: '⚔️ Saldırı', presets: [
    { label: '%6 Fiz.Hasar', stat: 'percentPhysicalDamage', value: 6 },
    { label: '%10 Fiz.Hasar', stat: 'percentPhysicalDamage', value: 10 },
    { label: '%6 Elem.Hasar', stat: 'percentElementalDamage', value: 6 },
    { label: '%6 Büyü', stat: 'percentSpellDamage', value: 6 },
    { label: '%4 Sld.Hızı', stat: 'percentAttackSpeed', value: 4 },
    { label: '%4 Büyü Hızı', stat: 'percentCastSpeed', value: 4 },
  ]},
  { title: '🎯 Kritik', presets: [
    { label: '%5 Krit Şansı', stat: 'percentCritChance', value: 5 },
    { label: '%10 Krit Hasarı', stat: 'percentCritDamage', value: 10 },
  ]},
  { title: '🏃 Utility', presets: [
    { label: '%4 Hareket', stat: 'percentMovementSpeed', value: 4 },
    { label: '%6 Mana', stat: 'percentMana', value: 6 },
    { label: '%5 Mana Regen', stat: 'percentManaRegen', value: 5 },
  ]},
  { title: '☠️ DoT', presets: [
    { label: '%6 DoT', stat: 'dotDamage', value: 6 },
    { label: '%3 Can Emme', stat: 'lifeLeechPercent', value: 3 },
    { label: '%3 Mana Emme', stat: 'manaLeechPercent', value: 3 },
  ]},
  { title: '👻 Minyon', presets: [
    { label: '%6 Minyon Hasar', stat: 'minionDamage', value: 6 },
    { label: '%6 Minyon Can', stat: 'minionLife', value: 6 },
  ]},
];

// Flat list for preset event handlers
const _PRESET_BONUSES = PRESET_GROUPS.flatMap(g => g.presets);
void _PRESET_BONUSES; // used in event handlers via data attributes

// ========================
// CSS
// ========================

const CSS = `
.pte-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  flex-direction: column;
  z-index: 1100;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
}

.pte-panel {
  background: linear-gradient(180deg, #1a1510, #0d0a08);
  border: 2px solid #3a3530;
  border-radius: 10px;
  width: 95vw; height: 90vh;
  margin: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pte-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 16px; border-bottom: 1px solid #3a3530; flex-shrink: 0;
}

.pte-header h2 { margin: 0; font-size: 18px; color: #ffd700; }

.pte-close {
  background: none; border: 1px solid #555; color: #f4e8d4; font-size: 18px;
  cursor: pointer; border-radius: 4px; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
}
.pte-close:hover { background: #a33; border-color: #f66; }

.pte-body {
  display: flex; flex: 1; overflow: hidden;
}

.pte-canvas-wrap {
  flex: 1; overflow: hidden; position: relative; background: #0a0a0a;
}

.pte-canvas-wrap canvas {
  display: block; cursor: crosshair;
}

.pte-right {
  width: 260px; min-width: 260px; border-left: 1px solid #3a3530;
  overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px;
  background: #111;
}

.pte-section {
  border: 1px solid #2a2520; border-radius: 6px; padding: 8px;
  background: rgba(20,18,14,0.8);
}

.pte-section-title {
  font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px;
  margin-bottom: 6px; border-bottom: 1px solid #2a2520; padding-bottom: 4px;
}

.pte-node-types, .pte-branches {
  display: flex; flex-wrap: wrap; gap: 4px;
}

.pte-btn {
  background: #1e1a16; border: 1px solid #3a3530; color: #f4e8d4;
  border-radius: 4px; padding: 4px 8px; cursor: pointer; font-size: 12px;
  display: flex; align-items: center; gap: 4px; transition: all 0.15s;
}
.pte-btn:hover { background: #2a2520; border-color: #555; }
.pte-btn.active { border-color: #ffd700; background: #2a2520; color: #ffd700; }
.pte-btn.danger { border-color: #a33; color: #f66; }
.pte-btn.danger:hover { background: #411; }

.pte-btn-sm {
  font-size: 10px; padding: 2px 5px;
}

.pte-field { margin-bottom: 6px; }
.pte-field label {
  display: block; font-size: 11px; color: #999; margin-bottom: 2px;
}
.pte-field input, .pte-field select, .pte-field textarea {
  width: 100%; background: #1a1510; border: 1px solid #3a3530; color: #f4e8d4;
  border-radius: 3px; padding: 4px 6px; font-size: 12px; box-sizing: border-box;
  font-family: inherit;
}
.pte-field textarea { resize: vertical; min-height: 40px; }

.pte-bonus-row {
  display: flex; align-items: center; gap: 4px; margin-bottom: 3px;
  font-size: 11px; background: #1a1510; border-radius: 3px; padding: 3px 5px;
}
.pte-bonus-row .pte-bonus-label { flex: 1; }
.pte-bonus-row .pte-bonus-val { width: 40px; text-align: right; color: #7f7; }
.pte-bonus-row .pte-bonus-rm {
  cursor: pointer; color: #a66; font-size: 13px; padding: 0 2px;
}
.pte-bonus-row .pte-bonus-rm:hover { color: #f66; }

.pte-presets {
  display: flex; flex-wrap: wrap; gap: 3px; margin-top: 4px;
}
.pte-presets .pte-btn { font-size: 10px; padding: 2px 5px; }

.pte-add-bonus {
  display: flex; gap: 4px; margin-top: 4px;
}
.pte-add-bonus select { flex: 1; font-size: 11px; }
.pte-add-bonus input { width: 45px; font-size: 11px; }

.pte-statusbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 4px 16px; border-top: 1px solid #3a3530; font-size: 11px;
  color: #888; flex-shrink: 0; background: #0d0a08;
}

.pte-tools {
  display: flex; flex-wrap: wrap; gap: 4px;
}
`;

// ========================
// Editor Class
// ========================

export class PassiveTreeEditorHTML {
  private el: HTMLDivElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private rightPanel: HTMLDivElement | null = null;
  private statusBar: HTMLDivElement | null = null;

  private visible = false;
  private nodes: Map<string, EditorNode> = new Map();
  private selectedNodeId: string | null = null;
  private nextNodeId = 1;

  // Tool state
  private activeNodeType: EditorNodeType = 'small';
  private activeBranch: BranchId | '' = '';

  // Grid/camera state
  private zoom = 1.0;
  private scrollX = 0;
  private scrollY = 0;
  private hoverGridX = -1;
  private hoverGridY = -1;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panScrollStartX = 0;
  private panScrollStartY = 0;

  // Connection preview
  private isConnecting = false;
  private connectMouseX = 0;
  private connectMouseY = 0;

  // Undo
  private undoStack: UndoAction[] = [];

  // Animation frame
  private rafId = 0;

  // Auto-save
  private lastSaveTime = 0;
  private static readonly STORAGE_KEY = 'passive_tree_editor';

  // Event listener refs for cleanup
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    // Pre-center scroll so (20,20) is roughly in the center
    this.scrollX = GRID_W * CELL_SIZE / 2 - 400;
    this.scrollY = GRID_H * CELL_SIZE / 2 - 300;
  }

  // ========================
  // Public API
  // ========================

  open(): void {
    if (this.visible) return;
    this.visible = true;
    this.loadFromStorage();
    this.buildDOM();
    this.attachEvents();
    this.startRenderLoop();
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.detachEvents();
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = 0; }
    if (this.el) { this.el.remove(); this.el = null; }
    if (this.styleEl) { this.styleEl.remove(); this.styleEl = null; }
    this.canvas = null;
    this.ctx = null;
    this.rightPanel = null;
    this.statusBar = null;
  }

  toggle(): void {
    if (this.visible) this.close(); else this.open();
  }

  isVisible(): boolean {
    return this.visible;
  }

  // ========================
  // DOM Building
  // ========================

  private buildDOM(): void {
    // Inject CSS
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = CSS;
    document.head.appendChild(this.styleEl);

    // Overlay
    this.el = document.createElement('div');
    this.el.className = 'pte-overlay';
    this.el.innerHTML = `
      <div class="pte-panel">
        <div class="pte-header">
          <h2>${ICONS.ui.edit} Pasif Ağaç Editörü (Grid)</h2>
          <button class="pte-close" data-action="close">${ICONS.ui.close}</button>
        </div>
        <div class="pte-body">
          <div class="pte-canvas-wrap">
            <canvas></canvas>
          </div>
          <div class="pte-right"></div>
        </div>
        <div class="pte-statusbar"></div>
      </div>
    `;

    document.body.appendChild(this.el);

    this.canvas = this.el.querySelector('canvas')!;
    this.ctx = this.canvas.getContext('2d')!;
    this.rightPanel = this.el.querySelector('.pte-right')!;
    this.statusBar = this.el.querySelector('.pte-statusbar')!;

    // Close button
    this.el.querySelector('[data-action="close"]')!.addEventListener('click', () => this.close());

    // Size canvas
    this.resizeCanvas();
    this.buildRightPanel();
    this.updateStatusBar();
  }

  private resizeCanvas(): void {
    if (!this.canvas) return;
    const wrap = this.canvas.parentElement!;
    const rect = wrap.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
  }

  // ========================
  // Right Panel
  // ========================

  private buildRightPanel(): void {
    if (!this.rightPanel) return;

    let html = '';

    // Node type selector
    html += `<div class="pte-section">
      <div class="pte-section-title">Yerleştir</div>
      <div class="pte-node-types">`;
    for (const t of NODE_TYPES) {
      const icon = ICONS.nodes[t as keyof typeof ICONS.nodes] || '?';
      const active = t === this.activeNodeType ? ' active' : '';
      html += `<button class="pte-btn${active}" data-set-type="${t}">${icon} ${NODE_TYPE_LABELS[t]}</button>`;
    }
    html += `</div></div>`;

    // Branch selector
    html += `<div class="pte-section">
      <div class="pte-section-title">Dal</div>
      <div class="pte-branches">`;
    for (const b of BRANCHES) {
      const icon = ICONS.branches[b as keyof typeof ICONS.branches] || '';
      const active = b === this.activeBranch ? ' active' : '';
      const col = BRANCH_COLORS[b] || '#555';
      html += `<button class="pte-btn${active}" data-set-branch="${b}" style="border-left: 3px solid ${col}">${icon} ${BRANCH_LABELS[b] || 'Yok'}</button>`;
    }
    html += `</div></div>`;

    // Selected node properties
    html += this.buildPropertiesHTML();

    // Tools
    html += `<div class="pte-section">
      <div class="pte-section-title">Araçlar</div>
      <div class="pte-tools">
        <button class="pte-btn" data-action="export">${ICONS.ui.export} Export JSON</button>
        <button class="pte-btn" data-action="import">${ICONS.ui.import} Import JSON</button>
        <button class="pte-btn" data-action="load-current">${ICONS.ui.load} Mevcut Yükle</button>
        <button class="pte-btn danger" data-action="clear">${ICONS.ui.delete} Temizle</button>
      </div>
    </div>`;

    this.rightPanel.innerHTML = html;
    this.attachRightPanelEvents();
  }

  private buildPropertiesHTML(): string {
    const node = this.selectedNodeId ? this.nodes.get(this.selectedNodeId) : null;
    if (!node) {
      return `<div class="pte-section">
        <div class="pte-section-title">Seçili Nod</div>
        <div style="color:#666;font-size:12px;">Nod seçmek için tıklayın</div>
      </div>`;
    }

    let html = `<div class="pte-section">
      <div class="pte-section-title">Seçili: ${node.name || node.id}</div>

      <div class="pte-field">
        <label>Ad</label>
        <input type="text" data-prop="name" value="${this.escapeAttr(node.name)}" />
      </div>

      <div class="pte-field">
        <label>Açıklama</label>
        <textarea data-prop="description">${this.escapeAttr(node.description)}</textarea>
      </div>

      <div class="pte-field">
        <label>Tip</label>
        <select data-prop="type">`;
    for (const t of NODE_TYPES) {
      html += `<option value="${t}"${node.type === t ? ' selected' : ''}>${NODE_TYPE_LABELS[t]}</option>`;
    }
    html += `</select></div>

      <div class="pte-field">
        <label>Dal</label>
        <select data-prop="branchId">
          <option value=""${node.branchId === '' ? ' selected' : ''}>Yok</option>`;
    for (const b of BRANCHES) {
      html += `<option value="${b}"${node.branchId === b ? ' selected' : ''}>${BRANCH_LABELS[b]}</option>`;
    }
    html += `</select></div>

      <div class="pte-field">
        <label>Kategori</label>
        <select data-prop="category">`;
    for (const c of CATEGORIES) {
      html += `<option value="${c}"${node.category === c ? ' selected' : ''}>${c}</option>`;
    }
    html += `</select></div>`;

    // Bonuses
    html += `<div class="pte-field">
        <label>Bonuslar</label>`;
    const bonusEntries = Object.entries(node.bonuses);
    for (const [stat, val] of bonusEntries) {
      const opt = STAT_OPTIONS.find(s => s.value === stat);
      const label = opt ? `${opt.icon} ${opt.label}` : stat;
      html += `<div class="pte-bonus-row">
        <span class="pte-bonus-label">${label}</span>
        <span class="pte-bonus-val">${val}</span>
        <span class="pte-bonus-rm" data-rm-bonus="${stat}">${ICONS.ui.remove}</span>
      </div>`;
    }
    html += `<div class="pte-add-bonus">
        <select data-bonus-stat>`;
    for (const s of STAT_OPTIONS) {
      html += `<option value="${s.value}">${s.icon} ${s.label}</option>`;
    }
    html += `</select>
        <input type="number" data-bonus-val value="5" style="width:45px" />
        <button class="pte-btn pte-btn-sm" data-action="add-bonus">${ICONS.ui.add}</button>
      </div>

      <div class="pte-presets">`;
    for (const group of PRESET_GROUPS) {
      html += `<div style="margin-top:4px"><span style="font-size:9px;color:#888">${group.title}</span><div style="display:flex;flex-wrap:wrap;gap:2px;margin-top:2px">`;
      for (const p of group.presets) {
        html += `<button class="pte-btn pte-btn-sm" data-preset-stat="${p.stat}" data-preset-val="${p.value}" title="${p.stat}: ${p.value}">${p.label}</button>`;
      }
      html += `</div></div>`;
    }
    html += `</div></div>`;

    // Keystone effect
    if (node.type === 'keystone') {
      html += `<div class="pte-field">
        <label>Keystone Efekti</label>
        <textarea data-prop="keystoneEffect">${this.escapeAttr(node.keystoneEffect || '')}</textarea>
      </div>`;
    }

    // Delete button
    html += `<button class="pte-btn danger" data-action="delete-node" style="margin-top:6px;width:100%">${ICONS.ui.delete} Sil</button>`;

    html += `</div>`;
    return html;
  }

  private attachRightPanelEvents(): void {
    if (!this.rightPanel) return;

    // Node type buttons
    this.rightPanel.querySelectorAll<HTMLElement>('[data-set-type]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.activeNodeType = btn.dataset.setType as EditorNodeType;
        this.buildRightPanel();
      });
    });

    // Branch buttons
    this.rightPanel.querySelectorAll<HTMLElement>('[data-set-branch]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.activeBranch = btn.dataset.setBranch as BranchId | '';
        this.buildRightPanel();
      });
    });

    // Property inputs
    this.rightPanel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-prop]').forEach(input => {
      let lastPushed = false;
      const handler = (e: Event) => {
        const node = this.selectedNodeId ? this.nodes.get(this.selectedNodeId) : null;
        if (!node) return;
        const prop = input.dataset.prop as string;
        // Push undo once per focus session (on first change)
        if (!lastPushed) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.pushUndo({ type: 'edit', data: { id: node.id, prop, oldValue: (node as any)[prop] } });
          lastPushed = true;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any)[prop] = input.value;
        // Fix 8: Empty name prevention
        if (prop === 'name' && !input.value.trim()) {
          node.name = `Node ${node.id}`;
        }
        // If type changed, rebuild to show/hide keystone field
        if (prop === 'type') this.buildRightPanel();
        if (e.type === 'change') this.autoSave();
      };
      input.addEventListener('change', handler);
      input.addEventListener('input', handler);
      input.addEventListener('focus', () => { lastPushed = false; });
    });

    // Remove bonus
    this.rightPanel.querySelectorAll<HTMLElement>('[data-rm-bonus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const node = this.selectedNodeId ? this.nodes.get(this.selectedNodeId) : null;
        if (!node) return;
        const stat = btn.dataset.rmBonus!;
        this.pushUndo({ type: 'edit', data: { id: node.id, prop: `bonus:${stat}`, oldValue: node.bonuses[stat] } });
        delete node.bonuses[stat];
        this.autoSave();
        this.buildRightPanel();
      });
    });

    // Add bonus
    const addBonusBtn = this.rightPanel.querySelector('[data-action="add-bonus"]');
    if (addBonusBtn) {
      addBonusBtn.addEventListener('click', () => this.addBonusFromUI());
    }

    // Preset bonuses
    this.rightPanel.querySelectorAll<HTMLElement>('[data-preset-stat]').forEach(btn => {
      btn.addEventListener('click', () => {
        const node = this.selectedNodeId ? this.nodes.get(this.selectedNodeId) : null;
        if (!node) return;
        const stat = btn.dataset.presetStat!;
        const val = parseFloat(btn.dataset.presetVal!);
        this.pushUndo({ type: 'edit', data: { id: node.id, prop: `bonus:${stat}`, oldValue: node.bonuses[stat] ?? null } });
        node.bonuses[stat] = (node.bonuses[stat] || 0) + val;
        this.autoSave();
        this.buildRightPanel();
      });
    });

    // Delete node
    const delBtn = this.rightPanel.querySelector('[data-action="delete-node"]');
    if (delBtn) {
      delBtn.addEventListener('click', () => {
        if (this.selectedNodeId) this.deleteNode(this.selectedNodeId);
      });
    }

    // Tools
    this.rightPanel.querySelector('[data-action="export"]')?.addEventListener('click', () => this.exportJSON());
    this.rightPanel.querySelector('[data-action="import"]')?.addEventListener('click', () => this.importJSON());
    this.rightPanel.querySelector('[data-action="load-current"]')?.addEventListener('click', () => this.loadCurrentTree());
    this.rightPanel.querySelector('[data-action="clear"]')?.addEventListener('click', () => {
      if (confirm('Tüm nodları silmek istediğinize emin misiniz?')) {
        this.pushUndo({ type: 'delete', data: this.serializeAll() });
        this.nodes.clear();
        this.selectedNodeId = null;
        this.autoSave();
        this.buildRightPanel();
      }
    });
  }

  private addBonusFromUI(): void {
    const node = this.selectedNodeId ? this.nodes.get(this.selectedNodeId) : null;
    if (!node || !this.rightPanel) return;
    const statSelect = this.rightPanel.querySelector<HTMLSelectElement>('[data-bonus-stat]');
    const valInput = this.rightPanel.querySelector<HTMLInputElement>('[data-bonus-val]');
    if (!statSelect || !valInput) return;
    const stat = statSelect.value;
    const val = parseFloat(valInput.value) || 0;
    if (val === 0) return;
    this.pushUndo({ type: 'edit', data: { id: node.id, prop: `bonus:${stat}`, oldValue: node.bonuses[stat] ?? null } });
    node.bonuses[stat] = (node.bonuses[stat] || 0) + val;
    this.autoSave();
    this.buildRightPanel();
  }

  // ========================
  // Events
  // ========================

  private attachEvents(): void {
    if (!this.canvas) return;

    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    this.boundKeyDown = this.onKeyDown.bind(this);
    window.addEventListener('keydown', this.boundKeyDown);

    window.addEventListener('resize', this.onResize);
  }

  private detachEvents(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.onMouseDown);
      this.canvas.removeEventListener('mousemove', this.onMouseMove);
      this.canvas.removeEventListener('mouseup', this.onMouseUp);
      this.canvas.removeEventListener('wheel', this.onWheel);
    }
    if (this.boundKeyDown) {
      window.removeEventListener('keydown', this.boundKeyDown);
      this.boundKeyDown = null;
    }
    window.removeEventListener('resize', this.onResize);
  }

  private onResize = (): void => {
    this.resizeCanvas();
  };

  private onMouseDown = (e: MouseEvent): void => {
    // Middle mouse: pan
    if (e.button === 1) {
      e.preventDefault();
      this.isPanning = true;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panScrollStartX = this.scrollX;
      this.panScrollStartY = this.scrollY;
      return;
    }

    // Right click: deselect
    if (e.button === 2) {
      this.selectedNodeId = null;
      this.isConnecting = false;
      this.buildRightPanel();
      return;
    }

    if (e.button !== 0) return;

    const [gx, gy] = this.canvasToGrid(e.offsetX, e.offsetY);
    if (gx < 0 || gx >= GRID_W || gy < 0 || gy >= GRID_H) return;

    const existingNode = this.getNodeAt(gx, gy);

    // If a node is selected and we click another node: toggle connection
    if (this.selectedNodeId && existingNode && existingNode.id !== this.selectedNodeId) {
      this.connectNodes(this.selectedNodeId, existingNode.id);
      // Also select the clicked node for chaining connections
      this.selectedNodeId = existingNode.id;
      this.buildRightPanel();
      return;
    }

    // Ctrl+click: move selected node to empty cell
    if (e.ctrlKey && this.selectedNodeId && !existingNode) {
      if (gx < 0 || gx >= GRID_W || gy < 0 || gy >= GRID_H) return;
      const node = this.nodes.get(this.selectedNodeId);
      if (node) {
        this.pushUndo({ type: 'move', data: { id: node.id, fromX: node.gridX, fromY: node.gridY, toX: gx, toY: gy } });
        node.gridX = gx;
        node.gridY = gy;
        this.autoSave();
      }
      return;
    }

    // Click on existing node (no other node selected): select it
    if (existingNode) {
      this.selectedNodeId = existingNode.id;
      this.buildRightPanel();
      return;
    }

    // Click on empty cell: place new node
    this.placeNode(gx, gy);
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (this.isPanning) {
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      this.scrollX = this.panScrollStartX - dx / this.zoom;
      this.scrollY = this.panScrollStartY - dy / this.zoom;
      return;
    }

    const [gx, gy] = this.canvasToGrid(e.offsetX, e.offsetY);
    this.hoverGridX = gx;
    this.hoverGridY = gy;

    // Track mouse for connection preview
    if (this.selectedNodeId && e.shiftKey) {
      this.isConnecting = true;
      this.connectMouseX = e.offsetX;
      this.connectMouseY = e.offsetY;
    } else {
      this.isConnecting = false;
    }

    this.updateStatusBar();
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 1) {
      this.isPanning = false;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const oldZoom = this.zoom;
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.zoom + delta));

    // Zoom toward cursor
    if (this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const worldX = this.scrollX + mx / oldZoom;
      const worldY = this.scrollY + my / oldZoom;
      this.scrollX = worldX - mx / this.zoom;
      this.scrollY = worldY - my / this.zoom;
    }

    this.updateStatusBar();
  };

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.visible) return;

    // Don't intercept when typing in inputs
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    switch (e.key) {
      case 'Delete':
      case 'Backspace':
        if (this.selectedNodeId) {
          this.deleteNode(this.selectedNodeId);
          e.preventDefault();
        }
        break;
      case 'Escape':
        this.selectedNodeId = null;
        this.isConnecting = false;
        this.buildRightPanel();
        e.preventDefault();
        break;
      case 'z':
        if (e.ctrlKey) { this.undo(); e.preventDefault(); }
        break;
      case '1': this.activeNodeType = 'small'; this.buildRightPanel(); break;
      case '2': this.activeNodeType = 'notable'; this.buildRightPanel(); break;
      case '3': this.activeNodeType = 'keystone'; this.buildRightPanel(); break;
      case '4': this.activeNodeType = 'attribute'; this.buildRightPanel(); break;
      case '5': this.activeNodeType = 'branch_start'; this.buildRightPanel(); break;
      case '6': this.activeNodeType = 'center'; this.buildRightPanel(); break;
    }
  }

  // ========================
  // Grid Helpers
  // ========================

  private canvasToGrid(cx: number, cy: number): [number, number] {
    const worldX = this.scrollX + cx / this.zoom;
    const worldY = this.scrollY + cy / this.zoom;
    const gx = Math.floor(worldX / CELL_SIZE);
    const gy = Math.floor(worldY / CELL_SIZE);
    return [gx, gy];
  }

  private getNodeAt(gx: number, gy: number): EditorNode | null {
    for (const node of this.nodes.values()) {
      if (node.gridX === gx && node.gridY === gy) return node;
    }
    return null;
  }

  // ========================
  // Node Operations
  // ========================

  private placeNode(gx: number, gy: number): void {
    if (gx < 0 || gx >= GRID_W || gy < 0 || gy >= GRID_H) return;
    const id = `node_${this.nextNodeId++}`;
    const node: EditorNode = {
      id,
      name: `${NODE_TYPE_LABELS[this.activeNodeType] || 'Node'} ${this.nodes.size + 1}`,
      description: '',
      type: this.activeNodeType,
      category: 'strength',
      branchId: this.activeBranch,
      gridX: gx,
      gridY: gy,
      connections: [],
      bonuses: {},
      isAttributeNode: this.activeNodeType === 'attribute',
    };
    this.nodes.set(id, node);
    this.pushUndo({ type: 'place', data: { id } });
    this.selectedNodeId = id;
    this.autoSave();
    this.buildRightPanel();
  }

  private deleteNode(id: string): void {
    const node = this.nodes.get(id);
    if (!node) return;

    // Store for undo
    this.pushUndo({ type: 'delete', data: { node: { ...node, connections: [...node.connections], bonuses: { ...node.bonuses } } } });

    // Remove connections referencing this node
    for (const other of this.nodes.values()) {
      const idx = other.connections.indexOf(id);
      if (idx >= 0) other.connections.splice(idx, 1);
    }

    this.nodes.delete(id);
    if (this.selectedNodeId === id) this.selectedNodeId = null;
    this.autoSave();
    this.buildRightPanel();
  }

  private connectNodes(aId: string, bId: string): void {
    if (aId === bId) return;
    const a = this.nodes.get(aId);
    const b = this.nodes.get(bId);
    if (!a || !b) return;

    // Toggle connection
    if (a.connections.includes(bId)) {
      a.connections = a.connections.filter(c => c !== bId);
      b.connections = b.connections.filter(c => c !== aId);
      this.pushUndo({ type: 'disconnect', data: { a: aId, b: bId } });
    } else {
      a.connections.push(bId);
      b.connections.push(aId);
      this.pushUndo({ type: 'connect', data: { a: aId, b: bId } });
    }
    this.autoSave();
  }

  // ========================
  // Undo
  // ========================

  private pushUndo(action: UndoAction): void {
    this.undoStack.push(action);
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
  }

  private undo(): void {
    const action = this.undoStack.pop();
    if (!action) return;

    const d = action.data as Record<string, unknown>;
    switch (action.type) {
      case 'place':
        this.nodes.delete(d.id as string);
        if (this.selectedNodeId === d.id) this.selectedNodeId = null;
        break;
      case 'delete':
        if (d.node) {
          const n = d.node as EditorNode;
          this.nodes.set(n.id, n);
        } else if (typeof d === 'string') {
          // Full clear undo - deserialize
          this.deserializeAll(d as unknown as string);
        }
        break;
      case 'move': {
        const node = this.nodes.get(d.id as string);
        if (node) { node.gridX = d.fromX as number; node.gridY = d.fromY as number; }
        break;
      }
      case 'connect': {
        const a = this.nodes.get(d.a as string);
        const b = this.nodes.get(d.b as string);
        if (a) a.connections = a.connections.filter(c => c !== d.b);
        if (b) b.connections = b.connections.filter(c => c !== d.a);
        break;
      }
      case 'disconnect': {
        const a = this.nodes.get(d.a as string);
        const b = this.nodes.get(d.b as string);
        if (a && !a.connections.includes(d.b as string)) a.connections.push(d.b as string);
        if (b && !b.connections.includes(d.a as string)) b.connections.push(d.a as string);
        break;
      }
      case 'edit': {
        const editNode = this.nodes.get(d.id as string);
        if (editNode) {
          const prop = d.prop as string;
          if (prop.startsWith('bonus:')) {
            const stat = prop.substring(6);
            if (d.oldValue === null || d.oldValue === undefined) {
              delete editNode.bonuses[stat];
            } else {
              editNode.bonuses[stat] = d.oldValue as number;
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editNode as any)[prop] = d.oldValue;
          }
        }
        break;
      }
    }
    this.autoSave();
    this.buildRightPanel();
  }

  // ========================
  // Rendering
  // ========================

  private startRenderLoop(): void {
    const loop = (): void => {
      this.render();
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private render(): void {
    const ctx = this.ctx;
    const canvas = this.canvas;
    if (!ctx || !canvas) return;

    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.save();

    // Apply zoom and scroll
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.scrollX, -this.scrollY);

    // Determine visible cell range
    const startCol = Math.max(0, Math.floor(this.scrollX / CELL_SIZE));
    const startRow = Math.max(0, Math.floor(this.scrollY / CELL_SIZE));
    const endCol = Math.min(GRID_W, Math.ceil((this.scrollX + w / this.zoom) / CELL_SIZE));
    const endRow = Math.min(GRID_H, Math.ceil((this.scrollY + h / this.zoom) / CELL_SIZE));

    // Draw grid lines
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 0.5;
    for (let c = startCol; c <= endCol; c++) {
      const x = c * CELL_SIZE;
      ctx.beginPath();
      ctx.moveTo(x, startRow * CELL_SIZE);
      ctx.lineTo(x, endRow * CELL_SIZE);
      ctx.stroke();
    }
    for (let r = startRow; r <= endRow; r++) {
      const y = r * CELL_SIZE;
      ctx.beginPath();
      ctx.moveTo(startCol * CELL_SIZE, y);
      ctx.lineTo(endCol * CELL_SIZE, y);
      ctx.stroke();
    }

    // Hover highlight
    if (this.hoverGridX >= 0 && this.hoverGridX < GRID_W && this.hoverGridY >= 0 && this.hoverGridY < GRID_H) {
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(
        this.hoverGridX * CELL_SIZE + 1,
        this.hoverGridY * CELL_SIZE + 1,
        CELL_SIZE - 2,
        CELL_SIZE - 2
      );
    }

    // Draw connections (with viewport culling)
    for (const node of this.nodes.values()) {
      // At least one end must be visible for the connection to render
      const nodeVisible = node.gridX >= startCol - 1 && node.gridX <= endCol && node.gridY >= startRow - 1 && node.gridY <= endRow;
      const ax = node.gridX * CELL_SIZE + CELL_SIZE / 2;
      const ay = node.gridY * CELL_SIZE + CELL_SIZE / 2;
      for (const connId of node.connections) {
        const other = this.nodes.get(connId);
        if (!other) continue;
        // Only draw once (lower id draws)
        if (node.id > connId) continue;
        const otherVisible = other.gridX >= startCol - 1 && other.gridX <= endCol && other.gridY >= startRow - 1 && other.gridY <= endRow;
        if (!nodeVisible && !otherVisible) continue;
        const bx = other.gridX * CELL_SIZE + CELL_SIZE / 2;
        const by = other.gridY * CELL_SIZE + CELL_SIZE / 2;
        ctx.strokeStyle = BRANCH_COLORS[node.branchId] || '#555';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }
    }

    // Connection preview (dashed line)
    if (this.isConnecting && this.selectedNodeId) {
      const selNode = this.nodes.get(this.selectedNodeId);
      if (selNode) {
        const ax = selNode.gridX * CELL_SIZE + CELL_SIZE / 2;
        const ay = selNode.gridY * CELL_SIZE + CELL_SIZE / 2;
        // Convert mouse pos back to world coords
        const mx = this.scrollX + this.connectMouseX / this.zoom;
        const my = this.scrollY + this.connectMouseY / this.zoom;
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(mx, my);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // Highlight connectable nodes when a node is selected
    if (this.selectedNodeId) {
      const selNode = this.nodes.get(this.selectedNodeId);
      if (selNode) {
        for (const node of this.nodes.values()) {
          if (node.id === this.selectedNodeId) continue;
          const isConnected = selNode.connections.includes(node.id);
          const cx = node.gridX * CELL_SIZE + CELL_SIZE / 2;
          const cy = node.gridY * CELL_SIZE + CELL_SIZE / 2;
          // Show green ring for unconnected, orange for connected (click to disconnect)
          ctx.beginPath();
          ctx.arc(cx, cy, CELL_SIZE / 2 - 2, 0, Math.PI * 2);
          ctx.strokeStyle = isConnected ? 'rgba(255,165,0,0.4)' : 'rgba(100,255,100,0.2)';
          ctx.lineWidth = isConnected ? 2 : 1;
          ctx.stroke();
        }
      }
    }

    // Draw nodes (with viewport culling)
    for (const node of this.nodes.values()) {
      if (node.gridX >= startCol - 1 && node.gridX <= endCol && node.gridY >= startRow - 1 && node.gridY <= endRow) {
        this.renderNode(ctx, node);
      }
    }

    ctx.restore();
  }

  private renderNode(ctx: CanvasRenderingContext2D, node: EditorNode): void {
    const cx = node.gridX * CELL_SIZE + CELL_SIZE / 2;
    const cy = node.gridY * CELL_SIZE + CELL_SIZE / 2;
    const baseSize = NODE_SIZES[node.type as keyof typeof NODE_SIZES] || 10;
    const radius = Math.min(baseSize * 1.0, CELL_SIZE / 2 - 4);
    const isSelected = node.id === this.selectedNodeId;

    // Fill
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = BRANCH_COLORS[node.branchId] || '#333';
    ctx.fill();

    // Border
    ctx.lineWidth = isSelected ? 3 : 1.5;
    ctx.strokeStyle = isSelected ? '#ffd700' : (NODE_TYPE_COLORS[node.type] || '#888');
    ctx.stroke();

    // Glow for selected
    if (isSelected) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 4, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.restore();
    }

    // Icon text
    const icon = ICONS.nodes[node.type as keyof typeof ICONS.nodes] || '?';
    ctx.fillStyle = '#fff';
    ctx.font = `${radius * 0.9}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(icon, cx, cy);

    // Label below
    if (this.zoom >= 0.7) {
      ctx.fillStyle = '#ccc';
      ctx.font = '8px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const label = node.name.length > 12 ? node.name.substring(0, 11) + '…' : node.name;
      ctx.fillText(label, cx, cy + radius + 3);
    }
  }

  // ========================
  // Status Bar
  // ========================

  private updateStatusBar(): void {
    if (!this.statusBar) return;
    const nodeCount = this.nodes.size;
    let connCount = 0;
    for (const n of this.nodes.values()) connCount += n.connections.length;
    connCount = Math.floor(connCount / 2); // each connection counted twice

    const cellLabel = (this.hoverGridX >= 0 && this.hoverGridX < GRID_W && this.hoverGridY >= 0 && this.hoverGridY < GRID_H)
      ? `(${this.hoverGridX}, ${this.hoverGridY})`
      : '—';

    let saveLabel = '';
    if (this.lastSaveTime > 0) {
      const agoMs = Date.now() - this.lastSaveTime;
      const agoMin = Math.floor(agoMs / 60000);
      saveLabel = agoMin < 1 ? 'Son kaydedilme: az önce' : `Son kaydedilme: ${agoMin} dakika önce`;
    }

    this.statusBar.textContent = `Hücre: ${cellLabel}  |  Nodlar: ${nodeCount}  |  Bağlantı: ${connCount}  |  Zoom: ${this.zoom.toFixed(1)}x${saveLabel ? '  |  ' + saveLabel : ''}`;
  }

  // ========================
  // Import / Export
  // ========================

  private exportJSON(): void {
    const passiveNodes: PassiveNode[] = [];
    for (const node of this.nodes.values()) {
      const pn: PassiveNode = {
        id: node.id,
        name: node.name,
        description: node.description,
        type: (node.type === 'attribute' ? 'small' : node.type) as PassiveNodeType,
        category: node.category,
        bonuses: node.bonuses as unknown as PassiveNode['bonuses'],
        position: { x: node.gridX * 50, y: node.gridY * 50 },
        connections: [...node.connections],
        isAttributeNode: node.isAttributeNode,
      };
      if (node.branchId) pn.branchId = node.branchId as BranchId;
      if (node.isAttributeNode) pn.attributeAmount = 5;
      if (node.keystoneEffect) pn.keystoneEffect = node.keystoneEffect;
      passiveNodes.push(pn);
    }

    const json = JSON.stringify(passiveNodes, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'passive_tree.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  private importJSON(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string) as PassiveNode[];
          this.loadFromPassiveNodes(data);
        } catch (e) {
          alert('JSON parse hatası: ' + (e as Error).message);
        }
      };
      reader.readAsText(file);
    });
    input.click();
  }

  private loadCurrentTree(): void {
    import('@shared/passives/PassiveTree').then((mod) => {
      const manager = new mod.PassiveTreeManager();
      const allNodes = manager.getAllNodes();
      if (allNodes && allNodes.length > 0) {
        this.loadFromPassiveNodes(allNodes);
      } else {
        alert('Mevcut ağaç bulunamadı');
      }
    }).catch(() => {
      alert('Ağaç yüklenemedi');
    });
  }

  private loadFromPassiveNodes(data: PassiveNode[]): void {
    this.nodes.clear();
    this.selectedNodeId = null;
    let maxId = 0;
    const usedIds = new Set<string>();

    for (const pn of data) {
      // Fix 6: Import validation - skip nodes missing required fields
      if (!pn.id || !pn.name || !pn.type || !pn.position) {
        console.warn('[PassiveTreeEditor] Skipping invalid node (missing required fields):', pn);
        continue;
      }

      // Fix 12: Duplicate ID prevention
      let nodeId = pn.id;
      if (usedIds.has(nodeId)) {
        let suffix = 2;
        while (usedIds.has(`${pn.id}_${suffix}`)) suffix++;
        nodeId = `${pn.id}_${suffix}`;
        console.warn(`[PassiveTreeEditor] Duplicate ID "${pn.id}" renamed to "${nodeId}"`);
      }
      usedIds.add(nodeId);

      // Convert pixel position to grid
      let gx = Math.round(pn.position.x / 50);
      let gy = Math.round(pn.position.y / 50);

      // Fix 7: Bounds validation on import
      gx = Math.max(0, Math.min(GRID_W - 1, gx));
      gy = Math.max(0, Math.min(GRID_H - 1, gy));

      const node: EditorNode = {
        id: nodeId,
        name: pn.name || `Node ${nodeId}`,
        description: pn.description || '',
        type: pn.isAttributeNode ? 'attribute' : pn.type,
        category: pn.category || 'strength',
        branchId: pn.branchId || '',
        gridX: gx,
        gridY: gy,
        connections: [...(pn.connections || [])],
        bonuses: Object.fromEntries(
          Object.entries(pn.bonuses || {}).filter(([, v]) => typeof v === 'number').map(([k, v]) => [k, v as number])
        ),
        isAttributeNode: pn.isAttributeNode || false,
        keystoneEffect: pn.keystoneEffect,
      };
      this.nodes.set(node.id, node);

      // Track max numeric id
      const m = node.id.match(/\d+/);
      if (m) maxId = Math.max(maxId, parseInt(m[0]));
    }

    this.nextNodeId = maxId + 1;
    this.autoSave();
    this.buildRightPanel();
  }

  // ========================
  // Serialization (for undo of clear)
  // ========================

  private serializeAll(): string {
    const arr: EditorNode[] = [];
    for (const n of this.nodes.values()) {
      arr.push({ ...n, connections: [...n.connections], bonuses: { ...n.bonuses } });
    }
    return JSON.stringify(arr);
  }

  private deserializeAll(json: string): void {
    try {
      const arr = JSON.parse(json) as EditorNode[];
      this.nodes.clear();
      for (const n of arr) {
        this.nodes.set(n.id, n);
      }
    } catch { /* ignore */ }
  }

  // ========================
  // Auto-save
  // ========================

  private autoSave(): void {
    try {
      const json = this.serializeAll();
      localStorage.setItem(PassiveTreeEditorHTML.STORAGE_KEY, json);
      this.lastSaveTime = Date.now();
      this.updateStatusBar();
    } catch { /* localStorage full or unavailable */ }
  }

  private loadFromStorage(): void {
    try {
      const json = localStorage.getItem(PassiveTreeEditorHTML.STORAGE_KEY);
      if (json && this.nodes.size === 0) {
        this.deserializeAll(json);
        this.lastSaveTime = Date.now();
      }
    } catch { /* ignore */ }
  }

  // ========================
  // Util
  // ========================

  private escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
